import React from 'react';
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { PatientCard } from '../API';

interface PatientCardSelectorProps {
  patientCards: PatientCard[];
  selectedCardId: string;
  onCardSelect: (cardId: string) => void;
  onCardDelete: (cardId: string) => void;
  isLoading?: boolean;
}

const PatientCardSelector: React.FC<PatientCardSelectorProps> = ({
  patientCards,
  selectedCardId,
  onCardSelect,
  onCardDelete,
  isLoading = false,
}) => {
  const handleCardSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    onCardSelect(event.target.value);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="section" aria-labelledby="patient-card-selector-title" sx={{ mb: 3 }}>
      <Typography id="patient-card-selector-title" component="h2" variant="h6" sx={{ mb: 1.5 }}>
        診察券を選択
      </Typography>
      <RadioGroup aria-labelledby="patient-card-selector-title" value={selectedCardId} onChange={handleCardSelect} sx={{ gap: 1.5 }}>
        {patientCards.map((card) => (
          <Box
            key={card.cardId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: selectedCardId === card.cardId ? 'primary.main' : 'divider',
              bgcolor: selectedCardId === card.cardId ? '#E1F1EF' : 'background.paper',
              borderRadius: "12px",
              pl: 1,
              pr: 1.5,
            }}
          >
            <FormControlLabel
              value={card.cardId}
              control={<Radio />}
              label={
                <Box sx={{ py: 1.75, minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {card.nickname || `診察券 ${card.patientCardNumber}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
                    診察券番号: {card.patientCardNumber} / 誕生月日: {card.patientBirth}
                  </Typography>
                </Box>
              }
              sx={{ flex: 1, minWidth: 0, m: 0, gap: 0.5, '& .MuiFormControlLabel-label': { minWidth: 0 } }}
            />
            <IconButton
              size="small"
              aria-label={`${card.nickname || `診察券 ${card.patientCardNumber}`}を削除`}
              onClick={() => onCardDelete(card.cardId)}
              sx={{ color: 'text.secondary', ml: 1, '&:hover': { color: 'error.main' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <FormControlLabel
          value="new"
          control={<Radio />}
          label={<Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>新しい診察券を登録</Typography>}
          sx={{ m: 0, px: 1, py: 0.5, border: '1px dashed', borderColor: selectedCardId === 'new' ? 'primary.main' : 'divider', borderRadius: "12px", bgcolor: selectedCardId === 'new' ? '#E1F1EF' : 'transparent' }}
        />
      </RadioGroup>
    </Box>
  );
};

export default PatientCardSelector;
